import { isEqual, omit } from 'lodash-es'

/**
 * Produce a plain, log-safe snapshot of a vue-router route.
 *
 * Raw route objects carry component instances inside `matched[].instances`
 * and `matched[].components`. Any global console wrapper that stringifies
 * log args enumerates those proxies and trips Vue's
 * "Avoid app logic that relies on enumerating keys on a component instance"
 * warning. This returns the full route minus the instance-bearing bits, so
 * `$logger.debug('...', serializeRoute(to))` logs everything useful without
 * the warning.
 *
 * @param {Object} route - A vue-router route (to/from).
 * @returns {(Object|*)} Plain object snapshot when given a route-like object;
 *   otherwise the input is returned unchanged (e.g. null/undefined/primitive).
 */
export function serializeRoute(route) {
  if (!route || typeof route !== 'object') {
    return route
  }
  const { fullPath, path, name, params, query, hash, redirectedFrom } = route
  return {
    fullPath,
    path,
    name,
    params,
    query,
    hash,
    // meta minus `previous` (the guard stores a whole route there → recursion)
    meta: route.meta ? omit(route.meta, 'previous') : route.meta,
    // names only — drop matched[].instances/.components (hold component proxies)
    matched: Array.isArray(route.matched) ? route.matched.map(r => r.name ?? r.path) : route.matched,
    redirectedFrom: redirectedFrom?.fullPath,
  }
}

/**
 * Check if the given path includes any of the specified slugs
 * @param {string} path - The path to check
 * @param {Array<string> | string} slugs - The slugs to check against
 * @returns {boolean} - True if the path includes any of the slugs, false otherwise
 */
export function findSlugInPath(path, slugs) {
  if (typeof slugs === 'string') {
    slugs = [slugs]
  }

  return slugs.find(slug => {
    const regex = new RegExp(`.*/(${slug})/?.*`)
    return regex.test(path)
  })
}

/**
 * Replace tokens in a path with actual values
 * @param {string} path - The path containing tokens (e.g., /retailers/{{id}}/orders/{{orderId}})
 * @param {Object} tokens - An object mapping token names to their replacement values (e.g., { id: '123', orderId: '456' })
 * @returns {string} - The path with tokens replaced by their corresponding values
 */
export function replacePathTokens(path, tokens) {
  let newPath = path
  Object.keys(tokens).forEach(key => {
    const regex = new RegExp(`{{${key}}}`, 'g')
    newPath = newPath.replace(regex, tokens[key])
  })
  return newPath
}

/**
 * Allow certain routes to be accessed when account setup is incomplete
 * @param {string} path - The path to check
 * @returns {boolean} - True if the path is whitelisted, false otherwise
 */
export function isAccountSetupWhitelistPath(path) {
  // Retailer (/r) and admin (/admin) sections have their own auth and setup flows
  // and are not part of the shopper account-setup experience. Never redirect them
  // to /account/setup. The optional `app/` prefix covers the /app alias on the
  // shared layout (nextPath is /app-prefixed on native); anchored so we don't
  // match shopper paths like /return or /reports. (PEG-921 made accountSetupIsDone
  // stricter, which otherwise bounced every authed non-shopper route to setup.)
  if (/^\/(app\/)?(r|admin)(\/|$)/.test(path)) return true
  return /\/(account\/setup|auth\/sign-out|terms|privacy|about|help|app\/return|app\/splash)(\/)?/.test(path)
}

export function isBlacklistPath(path) {
  const { featureFlags } = useFeatureFlagStore()
  // Defensive: this runs inside a beforeEach router guard, which fires during
  // router startup BEFORE flags are guaranteed loaded. A missing flag here must
  // never throw — a throw aborts router startup and white-screens the app.
  const slugs = featureFlags.blacklistSlugs?.value
  if (!slugs) return false
  return Boolean(findSlugInPath(path, slugs))
}

export function getPartnerSlugFromPath(path) {
  const match = /^\/(app\/)?p\/(.*?)(\/|$)/.exec(path || window.location.pathname)
  return match ? match[2] : null
}

/**
 * Compare two route paths to determine if they are equal, with options to include query and hash
 * @param {Object} to - The target route object (e.g., { path: '/home', query: { a: 1 }, hash: '#section' })
 * @param {Object} from - The current route object (e.g., { path: '/home', query: { a: 1 }, hash: '#section' })
 * @param {Object} options - Options to include query and hash in the comparison
 * @param {boolean} options.includeQuery - Whether to include query parameters in the comparison
 * @param {boolean} options.includeHash - Whether to include hash in the comparison
 * @returns {boolean} - True if the paths are equal based on the specified options, false otherwise
 */
export function toAndFromPathsAreEqual(to = {}, from = {}, options = {}) {
  const { includeQuery = false, includeHash = false } = options
  // Normalize paths by removing leading/trailing slashes
  let toValue = to.path?.replace(/^\/+|\/+$/g, '')
  let fromValue = from.path?.replace(/^\/+|\/+$/g, '')

  if (includeQuery && !isEqual(to.query, from.query)) {
    return false
  }
  if (includeHash && to.hash !== from.hash) {
    return false
  }

  return toValue === fromValue
}

/**
 * Sanitize a user-controlled `refTo` redirect target into a safe router
 * destination, preventing open-redirect via crafted query params (e.g.
 * `?refTo=https://evil.example` or `?refTo=//evil.example`).
 *
 * Only same-origin, single-leading-slash internal paths are honored. Anything
 * else — absolute URLs, scheme-relative `//host`, backslash tricks, protocol
 * schemes, or a missing/blank value — falls back to the ShopHome route.
 *
 * @param {*} refTo - The raw `route.query.refTo` value (string | undefined | other).
 * @param {Object} [fallback={ name: 'ShopHome' }] - Destination when refTo is unsafe.
 * @returns {(string|Object)} A safe internal path string, or the fallback route.
 */
export function safeInternalRedirect(refTo, fallback = { name: 'ShopHome' }) {
  if (typeof refTo !== 'string') {
    return fallback
  }
  const candidate = refTo.trim()
  // Must be a single-leading-slash internal path. Reject:
  //  - empty / non-path values
  //  - scheme-relative `//host` and backslash `/\host` (browsers treat as protocol-relative)
  //  - absolute URLs with a scheme (http:, https:, javascript:, data:, etc.)
  if (!candidate.startsWith('/') || candidate.startsWith('//') || candidate.startsWith('/\\')) {
    return fallback
  }
  // Defense-in-depth: a path that still parses as an absolute URL (has a scheme
  // or authority) is not a bare internal path — reject it.
  try {
    const parsed = new URL(candidate, window.location.origin)
    if (parsed.origin !== window.location.origin) {
      return fallback
    }
    // Re-emit the normalized same-origin path (keeps query + hash, drops host).
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return fallback
  }
}
